import { useState } from "react";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import {
  CHALLENGE_TYPE,
  TASK_EVENT_TYPE,
  TASK_RULE_FIELD,
} from "../../../../../types/gamePass";
import { useGetGamePassUserCampaignProgress } from "../../../../../queries/game-pass/useGetGamePassUserCampaignProgress";
import { useGetTasksList } from "../../../../../queries/game-pass/useGetTasksList";
import {
  useGetGamePassUserBets,
  useGetGamePassUserCasinoBets,
} from "../../../../../queries/game-pass/useGetGamePassUserBets";
import { useGetGamePassUserSocialEvents } from "../../../../../queries/game-pass/useGetGamePassUserSocialEvents";
import { normalizeCasinoGameId } from "../../../../../queries/game-pass/casinoGameNormalizers";
import { normalizeUserProgressResponse } from "../../../../../queries/game-pass/userProgressMappers";
import { formatCombinedOdds } from "../../../../../utils/BetTableUtils";
import RebetLoader from "../../../../shared/RebetLoader/RebetLoader";
import {
  formatHistoryResult,
  formatHistoryTimestamp,
} from "./monthlyChallengeHelpers";
import {
  buildLoginStreakHistoryRows,
  LOGIN_STREAK_HISTORY_COLUMNS,
} from "./loginStreakHistory";
import {
  useRewardCurrencyShortcode,
  useSecondaryCurrencyShortcode,
} from "./useRewardCurrencyLabel";
import MonthlyGroupSection from "./components/MonthlyGroupSection";
import LiveMonthlyChallengeCard from "./components/LiveMonthlyChallengeCard";

/** Same normalizer Seasons uses — campaign progress can arrive as
 *  `progress` / `challenges` / `groups` / `task_progress` (and nested
 *  envelopes). The thin `data.progress` read left Monthly at count 0
 *  whenever the admin payload used an alternate shape (FE-2544). */
const normalizeCampaignProgress = (progressResponse) => {
  if (!progressResponse) {
    return { progressEntries: [], enrollment: undefined };
  }
  const normalized = normalizeUserProgressResponse(progressResponse);
  return {
    progressEntries: Array.isArray(normalized?.data?.progress)
      ? normalized.data.progress
      : [],
    enrollment:
      normalized?.data?.enrollment ||
      progressResponse?.data?.enrollment ||
      progressResponse?.enrollment,
  };
};

const getBets = (betsResponse) => {
  const nested = betsResponse?.data?.bets || betsResponse?.bets;
  return Array.isArray(nested) ? nested : [];
};

const getEvents = (eventsResponse) => {
  const nested = eventsResponse?.data?.events || eventsResponse?.events;
  return Array.isArray(nested) ? nested : [];
};

/** BE `money_type` wire value for play-currency wagers (the same value
 *  `BET_CURRENCY_TO_MONEY_TYPE` maps to in the Create Season wizard — kept
 *  local so this view doesn't depend on that feature's form-data file). */
const REBET_COIN_MONEY_TYPE = "rebet_coin";

/** A new-style (FE-2441) Rebets challenge is a SPORTSBOOK task carrying a
 *  direction marker rule — `is_rebet` (user places re-bets) or, since FE-2535,
 *  `rebets_received` (others re-bet the user's picks). Same discriminator
 *  `monthlyDetailsMapper` uses. */
const isRebetsSportsbookTask = (task) =>
  Array.isArray(task?.rules) &&
  task.rules.some(
    (rule) =>
      rule?.field === TASK_RULE_FIELD.IS_REBET ||
      rule?.field === TASK_RULE_FIELD.REBETS_RECEIVED,
  );

const hasRule = (task, field) =>
  Array.isArray(task?.rules) && task.rules.some((rule) => rule?.field === field);

/** Tasks list ships `id`; progress ships `task_id`. Either identifies the row. */
const getTaskId = (task) => task?.id || task?.task_id;

/** SOCIAL tasks carry no `event_type` on the wire today — the create mappers
 *  send `null` — so the marker rule is the only reliable discriminator. */
const isFollowerTask = (task) =>
  hasRule(task, TASK_RULE_FIELD.IS_NEW_RELATIONSHIP);
const isReferralTask = (task) =>
  hasRule(task, TASK_RULE_FIELD.REFERRED_USER_MADE_QUALIFYING_DEPOSIT);
/** Login Streak: `daily_login` (authored), or a legacy `consecutive_days`
 *  rule without Seasons' pick-streak `shared_device_flag`. */
const isLoginStreakTask = (task) =>
  task?.event_type === TASK_EVENT_TYPE.DAILY_LOGIN ||
  (hasRule(task, TASK_RULE_FIELD.CONSECUTIVE_DAYS) &&
    !hasRule(task, TASK_RULE_FIELD.SHARED_DEVICE_FLAG));

/** Sportsbook History cells after Bet ID (pick type → bet amount).
 *  Pick Type uses `wager_type` (SINGLE / PARLAY / …); `bet_type` is fallback.
 *  `finalized` is a settlement flag ("1"/"0"), never a date or an amount — as a
 *  timestamp fallback it rendered "01 January 1970" (1 unix second) on every
 *  settled bet whose payload omitted `settled_at` (FE-2682).
 *  Date Settled is `settled_at` only. Bet Amount is `wager_amount` only. */
const sportsbookBetCells = (bet, currencyShortcode) => [
  bet.wager_type || bet.bet_type,
  bet.sport_name || bet.league_name,
  formatHistoryTimestamp(bet.created_at || bet.scheduled),
  formatHistoryTimestamp(bet.settled_at) || "-",
  formatCombinedOdds(bet.combined_odds ?? bet.odds),
  formatHistoryResult(bet.wager_amount, currencyShortcode) ?? "-",
];

const buildSportsbookHistoryRow = (bet, currencyShortcode) => ({
  id: bet.bet_id,
  searchText: bet.bet_id,
  cells: [bet.bet_id, ...sportsbookBetCells(bet, currencyShortcode)],
});

/** Sportsbook History: counted progress only (`redeemed_bets` /
 *  `redeemed_bet_ids`). `/bets` hydrates those ids — it is not a fallback. */
const buildSportsbookHistoryRows = ({
  progressTask,
  ownerBets = [],
  currencyShortcode,
}) => {
  const redeemedBets = Array.isArray(progressTask?.redeemed_bets)
    ? progressTask.redeemed_bets
    : [];
  const betById = new Map(
    ownerBets.filter((bet) => bet?.bet_id).map((bet) => [bet.bet_id, bet]),
  );

  if (redeemedBets.length > 0) {
    return redeemedBets.map((bet) =>
      buildSportsbookHistoryRow(
        { ...(betById.get(bet.bet_id) || {}), ...bet },
        currencyShortcode,
      ),
    );
  }

  const redeemedIds = progressTask?.redeemed_bet_ids ?? [];
  if (redeemedIds.length > 0) {
    return redeemedIds.map((betId) =>
      buildSportsbookHistoryRow(
        betById.get(betId) || { bet_id: betId },
        currencyShortcode,
      ),
    );
  }

  return [];
};

/** FE-2579 Rebet History columns (match Figma): Bet ID, Username of Rebetter,
 *  Date of Rebet, Sports, Rebet Type, Rebet Odds, Rebet Amount. */
const REBET_HISTORY_COLUMNS = [
  AppStrings.BET_ID,
  AppStrings.GAME_PASS_MONTHLY_COL_USERNAME_REBETTER,
  AppStrings.GAME_PASS_MONTHLY_COL_DATE_OF_REBET,
  AppStrings.GAME_PASS_MONTHLY_COL_SPORTS,
  AppStrings.GAME_PASS_MONTHLY_COL_REBET_TYPE,
  AppStrings.GAME_PASS_MONTHLY_COL_REBET_ODDS,
  AppStrings.GAME_PASS_MONTHLY_COL_REBET_AMOUNT,
];

const REBET_HISTORY_SEARCH_FILTERS = [
  { key: "betId", label: AppStrings.BET_ID },
  { key: "username", label: AppStrings.GAME_PASS_MONTHLY_COL_USERNAME_REBETTER },
  { key: "date", label: AppStrings.GAME_PASS_MONTHLY_COL_DATE_OF_REBET },
];

/** FE-2579 Casino History columns (match Figma): Bet ID, Provider, Game Title,
 *  Hand/Spin Count, Timestamp Per Session, Bet Size.
 *  Hand/Spin Count is hidden: progress / casino-bets do not currently ship a
 *  reliable count (`hand_count`, `spin_count`, `hand_spin_count`, `num_spins`,
 *  `num_hands` are empty). Restore the column + cell when BE data is available. */
const CASINO_HISTORY_COLUMNS = [
  AppStrings.BET_ID,
  AppStrings.USER_GAME_PASS_MONTHLY_COL_PROVIDER,
  AppStrings.USER_GAME_PASS_MONTHLY_COL_GAME_TITLE,
  // AppStrings.USER_GAME_PASS_MONTHLY_COL_HAND_SPIN_COUNT,
  AppStrings.USER_GAME_PASS_MONTHLY_COL_TIMESTAMP_PER_SESSION,
  AppStrings.USER_GAME_PASS_MONTHLY_COL_BET_SIZE,
];

/** FE-2579 Referral History columns (match Figma): Referred User ID, Signup
 *  Timestamp, Qualifying Action Completed, First Purchase. */
const REFERRAL_HISTORY_COLUMNS = [
  AppStrings.USER_GAME_PASS_MONTHLY_COL_REFERRED_USER_ID,
  AppStrings.USER_GAME_PASS_MONTHLY_COL_SIGNUP_TIMESTAMP,
  AppStrings.USER_GAME_PASS_MONTHLY_COL_QUALIFYING_ACTION,
  AppStrings.USER_GAME_PASS_MONTHLY_COL_FIRST_PURCHASE,
];

const REFERRAL_HISTORY_SEARCH_FILTERS = [
  {
    key: "referredUserId",
    label: AppStrings.USER_GAME_PASS_MONTHLY_COL_REFERRED_USER_ID,
  },
  {
    key: "username",
    label: AppStrings.GAME_PASS_MONTHLY_COL_USERNAME_REBETTER,
  },
  { key: "betId", label: AppStrings.BET_ID },
];

const formatYesNoWithOptionalDate = (value, timestamp) => {
  if (value == null || value === "") {
    return "-";
  }
  const isYes =
    value === true || value === 1 || value === "1" || value === "true";
  const isNo =
    value === false || value === 0 || value === "0" || value === "false";
  if (!isYes && !isNo) {
    return value;
  }
  const label = isYes ? AppStrings.YES : AppStrings.NO;
  const formatted = formatHistoryTimestamp(timestamp);
  return formatted ? `${label} ${formatted}` : label;
};

const readQualifyingDeposit = (value) => {
  if (value == null || value === "") {
    return { value: undefined, at: undefined };
  }
  if (typeof value === "object") {
    return {
      value: value.completed ?? value.qualifying_deposit ?? value.value,
      at: value.at || value.completed_at || value.timestamp,
    };
  }
  return { value, at: undefined };
};

const getReferralSocialEvents = (socialEvents) =>
  socialEvents.filter(
    (event) =>
      !event.event_type ||
      event.event_type === TASK_EVENT_TYPE.USER_REFERRED,
  );

/** Referral History: Ahmad's `distinct_users[]` (`referred_at` +
 *  `qualifying_deposit`) + task `count`. `/social-events` only hydrates
 *  First Purchase on those users — it is not a row fallback. */
const buildReferralHistoryRow = (user, event) => {
  const payload = event?.payload || {};
  const referredUserId =
    user?.user_id || payload.referred_user_id || event?.user_id;
  const signupTimestamp = formatHistoryTimestamp(
    user?.referred_at || payload.signup_timestamp,
  );
  const qualifying = readQualifyingDeposit(user?.qualifying_deposit);
  return {
    id: event?.event_id || referredUserId,
    searchValues: {
      referredUserId,
      username: user?.username || payload.referred_username || payload.username,
      betId: payload.bet_id,
    },
    cells: [
      referredUserId ?? "-",
      signupTimestamp || "-",
      formatYesNoWithOptionalDate(
        qualifying.value ??
          payload.qualifying_action_completed ??
          payload.won,
        qualifying.at ||
          payload.qualifying_action_completed_at ||
          payload.qualifying_action_at,
      ),
      formatYesNoWithOptionalDate(
        user?.first_purchase ??
          payload.first_purchase ??
          payload.first_purchase_completed,
        user?.first_purchase_at ||
          payload.first_purchase_at ||
          payload.first_purchase_timestamp,
      ),
    ],
  };
};

const buildReferralHistoryRows = ({ progressTask, socialEvents = [] }) => {
  const referralEvents = getReferralSocialEvents(socialEvents);
  const distinctUsers = Array.isArray(progressTask?.distinct_users)
    ? progressTask.distinct_users
    : [];

  return distinctUsers.map((user) => {
    const event = referralEvents.find(
      (item) =>
        (item.payload?.referred_user_id || item.user_id) === user.user_id,
    );
    return buildReferralHistoryRow(user, event);
  });
};

/** FE-2582 Follower History columns (match Figma): Username of Follow,
 *  User ID of Follow, Date of First Follow, Counted for Current Month. */
const FOLLOWER_HISTORY_COLUMNS = [
  AppStrings.USER_GAME_PASS_MONTHLY_COL_USERNAME_FOLLOW,
  AppStrings.GAME_PASS_MONTHLY_COL_USER_ID_OF_FOLLOW,
  AppStrings.USER_GAME_PASS_MONTHLY_COL_DATE_FIRST_FOLLOW,
  AppStrings.GAME_PASS_MONTHLY_COL_COUNTED_FOR_CURRENT_MONTH,
];

const FOLLOWER_HISTORY_SEARCH_FILTERS = [
  {
    key: "username",
    label: AppStrings.USER_GAME_PASS_MONTHLY_COL_USERNAME_FOLLOW,
  },
  {
    key: "userId",
    label: AppStrings.GAME_PASS_MONTHLY_COL_USER_ID_OF_FOLLOW,
  },
];

const isCountedYes = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const getFollowerSocialEvents = (socialEvents) =>
  socialEvents.filter(
    (event) =>
      !event.event_type ||
      event.event_type === TASK_EVENT_TYPE.USER_FOLLOWED,
  );

/** Follower History: Ahmad's `distinct_users[]` (`first_follow_at` +
 *  `counted`) + task `count`. `/social-events` only hydrates those users. */
const buildFollowerHistoryRow = (user, event) => {
  const payload = event?.payload || {};
  const userId =
    user?.user_id || payload.follower_user_id || payload.user_id;
  const username =
    user?.username || payload.username || payload.follower_username;
  const firstFollow = formatHistoryTimestamp(
    user?.first_follow_at ||
      payload.first_follow_at ||
      (user ? undefined : event?.occurred_at),
  );
  const counted = user?.counted ?? payload.counted;
  return {
    id: event?.event_id || userId,
    counted: counted == null ? null : isCountedYes(counted),
    searchValues: { username, userId },
    cells: [
      username || "-",
      userId || "-",
      firstFollow || "-",
      formatYesNoWithOptionalDate(counted),
    ],
  };
};

const buildFollowerHistoryRows = ({ progressTask, socialEvents = [] }) => {
  const followEvents = getFollowerSocialEvents(socialEvents);
  const distinctUsers = Array.isArray(progressTask?.distinct_users)
    ? progressTask.distinct_users
    : [];

  return distinctUsers.map((user) => {
    const event = followEvents.find(
      (item) =>
        (item.payload?.follower_user_id || item.payload?.user_id) ===
        user.user_id,
    );
    return buildFollowerHistoryRow(user, event);
  });
};

// Hidden with the Hand/Spin Count column — BE does not currently send a
// usable count on casino history rows. Restore with the column when it does.
// const casinoHandSpinCount = (bet) =>
//   bet.hand_count ??
//   bet.spin_count ??
//   bet.hand_spin_count ??
//   bet.num_spins ??
//   bet.num_hands ??
//   "-";

/** Game Title: Ahmad's best-effort `game_name` on the casino bet, then
 *  `game_title`, then the raw `game_id`. No catalogue walk — that fans out
 *  into dozens of `/casino-games` pages. `game_type` is a category, not a title. */
const casinoGameTitle = (bet) => {
  const apiName = (bet?.game_name || bet?.game_title || "").trim();
  if (apiName) {
    return apiName;
  }
  return normalizeCasinoGameId(bet?.game_id) || "-";
};

const buildCasinoHistoryRow = (bet, currencyShortcode) => {
  const timestamp = formatHistoryTimestamp(bet.created_at || bet.settled_at);
  const provider = bet.provider || bet.provider_name || "-";
  const gameTitle = casinoGameTitle(bet);
  return {
    id: bet.bet_id,
    searchText: bet.bet_id,
    cells: [
      bet.bet_id,
      provider,
      gameTitle,
      // casinoHandSpinCount(bet),
      timestamp,
      formatHistoryResult(bet.wager ?? bet.amount, currencyShortcode) ?? "-",
    ],
  };
};

const mergeCasinoBet = (primary, fallback) => {
  if (!fallback) {
    return primary;
  }
  return {
    ...fallback,
    ...primary,
    game_name: primary.game_name || primary.game_title || fallback.game_name,
    game_title: primary.game_title || fallback.game_title,
    game_id: primary.game_id || fallback.game_id,
    provider: primary.provider || fallback.provider,
    provider_name: primary.provider_name || fallback.provider_name,
  };
};

/** Casino History: counted progress only. `/casino-bets` hydrates those
 *  ids — it is not a fallback list. */
const buildCasinoHistoryRows = ({
  progressTask,
  casinoBets = [],
  currencyShortcode,
}) => {
  const redeemedBets = Array.isArray(progressTask?.redeemed_bets)
    ? progressTask.redeemed_bets
    : [];
  const casinoBetById = new Map(
    casinoBets.filter((bet) => bet?.bet_id).map((bet) => [bet.bet_id, bet]),
  );

  if (redeemedBets.length > 0) {
    return redeemedBets.map((bet) =>
      buildCasinoHistoryRow(
        mergeCasinoBet(bet, casinoBetById.get(bet.bet_id)),
        currencyShortcode,
      ),
    );
  }

  const redeemedIds = progressTask?.redeemed_bet_ids ?? [];
  if (redeemedIds.length > 0) {
    return redeemedIds.map((betId) =>
      buildCasinoHistoryRow(
        casinoBetById.get(betId) || { bet_id: betId },
        currencyShortcode,
      ),
    );
  }

  return [];
};

/** One Rebet History row, shared by the `rebets_received` (progress
 *  `redeemed_bets`) and legacy `is_rebet` (bets endpoint) paths. `rebetter` is
 *  the resolved username of whoever re-bet the pick — undefined renders "-" in
 *  the cell but is simply absent from the searchable values. */
const buildRebetHistoryRow = (bet, rebetter, shortcodeForMoneyType) => {
  const dateOfRebet = formatHistoryTimestamp(bet.created_at || bet.scheduled);
  return {
    id: bet.bet_id,
    searchValues: {
      betId: bet.bet_id,
      username: rebetter,
      date: dateOfRebet,
    },
    cells: [
      bet.bet_id,
      rebetter ?? "-",
      dateOfRebet,
      bet.sport_name || bet.league_name || "-",
      bet.wager_type || bet.bet_type || "-",
      bet.odds || bet.combined_odds || "-",
      formatHistoryResult(
        bet.wager_amount ?? bet.amount ?? bet.wager,
        shortcodeForMoneyType(bet.money_type),
      ) ?? "-",
    ],
  };
};

/** Placeholder Rebet History row when only a bet id is known (a
 *  `rebets_received` progress task that shipped `redeemed_bet_ids` but no
 *  `redeemed_bets`). One id cell, the rest are "-" sentinels. */
const buildRebetHistoryIdRow = (betId, rebetter) => ({
  id: betId,
  searchValues: { betId, username: rebetter },
  cells: [betId, rebetter ?? "-", "-", "-", "-", "-", "-"],
});

const displayNameFromDistinctUser = (user) => {
  const username = typeof user?.username === "string" ? user.username.trim() : "";
  if (username) {
    return username;
  }
  const fullName = [user?.first_name, user?.last_name]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ")
    .trim();
  return fullName || undefined;
};

const usernameFromDistinctUsers = (progressTask, bet) => {
  const users = (progressTask?.distinct_users ?? []).filter(Boolean);
  if (users.length === 0) {
    return undefined;
  }
  const betUserId = bet?.user_id || bet?.source_user_id;
  const matched = betUserId
    ? users.find((user) => user?.user_id === betUserId)
    : undefined;
  if (matched) {
    return displayNameFromDistinctUser(matched);
  }
  // redeemed_bets[].user_id is often the pick owner, not the rebetter.
  // One distinct user is the rebetter Ahmad ships on the progress task.
  if (users.length === 1) {
    return displayNameFromDistinctUser(users[0]);
  }
  if (betUserId) {
    return undefined;
  }
  return displayNameFromDistinctUser(
    users.find((user) => displayNameFromDistinctUser(user)),
  );
};

/** Username of Rebetter: progress `distinct_users[].username` first (Ahmad).
 *  Never put a user_id in this column. */
const resolveRebetter = (bet, progressTask) => {
  const fromDistinct = usernameFromDistinctUsers(progressTask, bet);
  if (fromDistinct) {
    return fromDistinct;
  }
  const fromBet = (bet?.username || bet?.source_username || "").trim();
  if (
    fromBet &&
    fromBet !== bet?.user_id &&
    fromBet !== bet?.source_user_id
  ) {
    return fromBet;
  }
  return undefined;
};

/** Rebet History: counted progress only (`redeemed_bets` / `redeemed_bet_ids`
 *  + `distinct_users` for Username of Rebetter). No eligible-bets — that
 *  endpoint is seasonal tasks, not monthly campaigns. */
const buildRebetHistoryRows = ({
  progressTask,
  ownerBets = [],
  rebetterBets = [],
  shortcodeForMoneyType,
}) => {
  const hydratePool = [...rebetterBets, ...ownerBets];
  const redeemedBets = Array.isArray(progressTask?.redeemed_bets)
    ? progressTask.redeemed_bets
    : [];
  if (redeemedBets.length > 0) {
    return redeemedBets.map((bet) => {
      const hydrated = hydratePool.find((item) => item.bet_id === bet.bet_id);
      const merged = hydrated ? { ...hydrated, ...bet } : bet;
      return buildRebetHistoryRow(
        merged,
        resolveRebetter(merged, progressTask),
        shortcodeForMoneyType,
      );
    });
  }

  const redeemedIds = progressTask?.redeemed_bet_ids ?? [];
  if (redeemedIds.length > 0) {
    return redeemedIds.map((betId) => {
      const bet = hydratePool.find((item) => item.bet_id === betId);
      if (bet) {
        return buildRebetHistoryRow(
          bet,
          resolveRebetter(bet, progressTask),
          shortcodeForMoneyType,
        );
      }
      return buildRebetHistoryIdRow(
        betId,
        resolveRebetter(undefined, progressTask),
      );
    });
  }

  return [];
};

const CampaignMonthGroup = ({
  campaign,
  userId,
  title,
  dateRange,
  status,
  defaultOpen = false,
  readOnly = false,
  testId,
}) => {
  const campaignId = campaign?.campaign_id || campaign?.id;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const currencyShortcode = useRewardCurrencyShortcode();
  const secondaryCurrencyShortcode = useSecondaryCurrencyShortcode();
  // A rebet wagered in the play currency (`rebet_coin`) is labeled with the
  // secondary shortcode; cash money types (incl. free cash) with the primary.
  const shortcodeForMoneyType = (moneyType) =>
    moneyType === REBET_COIN_MONEY_TYPE
      ? secondaryCurrencyShortcode
      : currencyShortcode;
  // Progress + tasks are per-campaign. Fetch only while the month group is
  // open so a long history list does not fire N pairs on first paint.
  const shouldFetchDetails = isOpen && Boolean(userId && campaignId);
  const progressQuery = useGetGamePassUserCampaignProgress(userId, campaignId, {
    enabled: shouldFetchDetails,
  });
  const tasksQuery = useGetTasksList({
    campaignId,
    enabled: shouldFetchDetails,
  });

  const { progressEntries, enrollment } = normalizeCampaignProgress(
    progressQuery.data,
  );
  const tasks = tasksQuery.data?.tasks ?? [];

  const needsSportsbook =
    tasks.some((task) => task.data_source === "SPORTSBOOK") ||
    progressEntries.some((entry) =>
      (entry.tasks ?? []).some(
        (task) =>
          task.challenge_type === CHALLENGE_TYPE.SPORTSBOOK ||
          task.challenge_type === CHALLENGE_TYPE.REBET,
      ),
    );
  const needsCasino =
    tasks.some((task) => task.data_source === "CASINO") ||
    progressEntries.some((entry) =>
      (entry.tasks ?? []).some(
        (task) => task.challenge_type === CHALLENGE_TYPE.CASINO,
      ),
    );
  const needsSocial =
    tasks.some(
      (task) => task.data_source === "SOCIAL" || isLoginStreakTask(task),
    ) ||
    progressEntries.some((entry) =>
      (entry.tasks ?? []).some(
        (task) =>
          task.challenge_type === CHALLENGE_TYPE.REFERRAL ||
          task.challenge_type === CHALLENGE_TYPE.FOLLOWER ||
          task.challenge_type === CHALLENGE_TYPE.LOGIN ||
          task.challenge_type === CHALLENGE_TYPE.SOCIAL ||
          isLoginStreakTask(task),
      ),
    );
  const progressTasks = progressEntries.flatMap((entry) => entry.tasks ?? []);
  const hasRedeemedBetSource = (task) =>
    (Array.isArray(task?.redeemed_bets) && task.redeemed_bets.length > 0) ||
    (Array.isArray(task?.redeemed_bet_ids) && task.redeemed_bet_ids.length > 0);
  const needsSportsbookHydration =
    needsSportsbook && progressTasks.some(hasRedeemedBetSource);
  const needsCasinoHydration =
    needsCasino &&
    progressTasks.some(
      (task) =>
        (task.challenge_type === CHALLENGE_TYPE.CASINO ||
          !task.challenge_type) &&
        hasRedeemedBetSource(task),
    );
  // Inbound rebets are placed by OTHER users. Hydrate row details from the
  // rebetter's `/bets` only when progress already counted those bets.
  const rebetterUserId = progressTasks
    .filter(
      (task) =>
        task.challenge_type === CHALLENGE_TYPE.REBET &&
        hasRedeemedBetSource(task),
    )
    .flatMap((task) => task.distinct_users ?? [])
    .map((user) => user.user_id)
    .find(Boolean);

  const betsQuery = useGetGamePassUserBets(userId, {
    enabled: Boolean(userId) && needsSportsbookHydration,
  });
  const rebetterBetsQuery = useGetGamePassUserBets(rebetterUserId, {
    enabled: Boolean(rebetterUserId),
  });
  const casinoBetsQuery = useGetGamePassUserCasinoBets(userId, {
    enabled: Boolean(userId) && needsCasinoHydration,
  });
  const socialEventsQuery = useGetGamePassUserSocialEvents(userId, {
    enabled: Boolean(userId) && needsSocial,
  });

  const casinoBets = getBets(casinoBetsQuery.data);

  const isLoading = progressQuery.isLoading || tasksQuery.isLoading;

  const challengeIds = [
    ...new Set([
      ...progressEntries.map((entry) => entry.challenge_id).filter(Boolean),
      ...tasks.map((task) => task.challenge_id).filter(Boolean),
    ]),
  ];

  // Keep the group when the campaign has authored tasks and/or user progress.
  // Do not require enrollment.campaign_id — that field is often absent today.
  const hasContent =
    Boolean(enrollment) ||
    progressEntries.length > 0 ||
    tasks.length > 0 ||
    challengeIds.length > 0;

  // Both failed → always error. One failed with no usable content → error so
  // the group does not silently disappear. One failed but the other still has
  // data → keep rendering that partial content for support.
  const isError =
    (progressQuery.isError && tasksQuery.isError) ||
    ((progressQuery.isError || tasksQuery.isError) && !hasContent);

  if (isOpen && !isLoading && !isError && !hasContent) {
    return null;
  }

  return (
    <MonthlyGroupSection
      title={title}
      status={status}
      dateRange={dateRange}
      open={isOpen}
      onOpenChange={setIsOpen}
      testId={testId}
    >
      {isLoading && (
        <div
          className="user-manager-game-pass-monthly-group-loading"
          data-testid={`${testId}-loading`}
        >
          <RebetLoader height="100px" width="100px" />
        </div>
      )}
      {!isLoading && isError && (
        <p
          className="user-manager-game-pass-monthly-group-error"
          data-testid={`${testId}-error`}
        >
          {AppStrings.GAME_PASS_MONTHLY_LOAD_ERROR_COPY}
        </p>
      )}
      {!isLoading &&
        !isError &&
        challengeIds.map((challengeId) => {
          const progressEntry =
            progressEntries.find((entry) => entry.challenge_id === challengeId) ||
            null;
          const challengeTasks = tasks.filter(
            (task) => task.challenge_id === challengeId,
          );
          const task =
            challengeTasks.find(
              (item) => Array.isArray(item.tiers) && item.tiers.length > 0,
            ) ||
            challengeTasks[0] ||
            null;
          const configTaskId = getTaskId(task);
          const progressTask =
            progressEntry?.tasks?.find(
              (item) => item.task_id && item.task_id === configTaskId,
            ) ||
            progressEntry?.tasks?.[0] ||
            null;

          const dataSource = task?.data_source || progressTask?.data_source;
          // FE-2579: the BE now classifies each task (`tasks[].challenge_type`
          // on the progress response). Prefer it; fall back to the
          // data_source + marker-rule heuristic for payloads shipped before it
          // lands.
          const challengeType =
            progressTask?.challenge_type || task?.challenge_type;
          // A Rebets challenge. The BE `challenge_type` is authoritative (it
          // ships neither `data_source` nor rule markers on the progress task —
          // FE-2579); the sportsbook marker heuristic stays as a fallback for
          // payloads shipped before the field landed.
          const isRebetChallenge =
            challengeType === CHALLENGE_TYPE.REBET ||
            (dataSource === "SPORTSBOOK" && isRebetsSportsbookTask(task));
          const isCasinoChallenge =
            challengeType === CHALLENGE_TYPE.CASINO || dataSource === "CASINO";
          const isSportsbookChallenge =
            !isRebetChallenge &&
            (challengeType === CHALLENGE_TYPE.SPORTSBOOK ||
              dataSource === "SPORTSBOOK");
          // SOCIAL tasks ship `event_type: null`, so fall back to the marker
          // rule to pick the right history table (Follow vs Referral).
          const eventType =
            task?.event_type ||
            progressTask?.event_type ||
            (isFollowerTask(task) || challengeType === CHALLENGE_TYPE.FOLLOWER
              ? TASK_EVENT_TYPE.USER_FOLLOWED
              : isReferralTask(task)
                ? TASK_EVENT_TYPE.USER_REFERRED
                : isLoginStreakTask(task) ||
                    challengeType === CHALLENGE_TYPE.LOGIN
                  ? TASK_EVENT_TYPE.DAILY_LOGIN
                  : undefined);
          const isReferralChallenge =
            challengeType === CHALLENGE_TYPE.REFERRAL ||
            eventType === TASK_EVENT_TYPE.USER_REFERRED ||
            isReferralTask(task);
          const isFollowerChallenge =
            challengeType === CHALLENGE_TYPE.FOLLOWER ||
            eventType === TASK_EVENT_TYPE.USER_FOLLOWED ||
            isFollowerTask(task);
          const isLoginChallenge =
            challengeType === CHALLENGE_TYPE.LOGIN ||
            eventType === TASK_EVENT_TYPE.DAILY_LOGIN ||
            isLoginStreakTask(task);

          let historyRows = [];
          let historyColumns = [];
          let historyLabel = "";
          let historyEmptyText = AppStrings.USER_GAME_PASS_MONTHLY_HISTORY_EMPTY;
          let historySearchPlaceholder;
          let historySearchFilters;
          let historyCountedFilterLabel;

          if (isRebetChallenge) {
            // FE-2579: Rebet History matches the Figma design — Bet ID,
            // Username of Rebetter, Date of Rebet, Sports, Rebet Type, Rebet
            // Odds, Rebet Amount. Search filters follow the visible columns.
            historyLabel = AppStrings.USER_GAME_PASS_MONTHLY_REBET_HISTORY;
            historyEmptyText = AppStrings.USER_GAME_PASS_MONTHLY_HISTORY_EMPTY;
            historySearchPlaceholder = AppStrings.SEARCH;
            historySearchFilters = REBET_HISTORY_SEARCH_FILTERS;
            historyColumns = REBET_HISTORY_COLUMNS;
            historyRows = buildRebetHistoryRows({
              progressTask,
              ownerBets: getBets(betsQuery.data),
              rebetterBets: getBets(rebetterBetsQuery.data),
              shortcodeForMoneyType,
            });
          } else if (isSportsbookChallenge) {
            historyLabel = AppStrings.USER_GAME_PASS_MONTHLY_SPORTSBOOK_HISTORY;
            historyEmptyText =
              AppStrings.USER_GAME_PASS_MONTHLY_SPORTSBOOK_HISTORY_EMPTY;
            historySearchPlaceholder = AppStrings.SEARCH_BY_BET_ID;
            historyColumns = [
              AppStrings.BET_ID,
              AppStrings.USER_GAME_PASS_MONTHLY_COL_PICK_TYPE,
              AppStrings.USER_GAME_PASS_MONTHLY_COL_SPORT,
              AppStrings.USER_GAME_PASS_MONTHLY_COL_DATE_PLACED,
              AppStrings.USER_GAME_PASS_MONTHLY_COL_DATE_SETTLED,
              AppStrings.USER_GAME_PASS_MONTHLY_COL_MIN_ODDS_OF_BET,
              AppStrings.USER_GAME_PASS_TASK_BET_AMOUNT,
            ];
            historyRows = buildSportsbookHistoryRows({
              progressTask,
              ownerBets: getBets(betsQuery.data),
              currencyShortcode,
            });
          } else if (isCasinoChallenge) {
            historyLabel = AppStrings.USER_GAME_PASS_MONTHLY_CASINO_HISTORY;
            historyEmptyText =
              AppStrings.USER_GAME_PASS_MONTHLY_CASINO_HISTORY_EMPTY;
            // FE-2438: Bet ID search only — same as Pick History (FE-2435).
            historySearchPlaceholder = AppStrings.SEARCH_BY_BET_ID;
            historyColumns = CASINO_HISTORY_COLUMNS;
            historyRows = buildCasinoHistoryRows({
              progressTask,
              casinoBets,
              currencyShortcode,
            });
          } else if (isReferralChallenge) {
            historyLabel = AppStrings.USER_GAME_PASS_MONTHLY_REFERRAL_HISTORY;
            historyEmptyText =
              AppStrings.USER_GAME_PASS_MONTHLY_REFERRAL_HISTORY_EMPTY;
            historySearchPlaceholder = AppStrings.SEARCH;
            historySearchFilters = REFERRAL_HISTORY_SEARCH_FILTERS;
            historyColumns = REFERRAL_HISTORY_COLUMNS;
            historyRows = buildReferralHistoryRows({
              progressTask,
              socialEvents: getEvents(socialEventsQuery.data),
            });
          } else if (isFollowerChallenge) {
            historyLabel = AppStrings.GAME_PASS_MONTHLY_FOLLOWER_HISTORY;
            historyEmptyText =
              AppStrings.USER_GAME_PASS_MONTHLY_FOLLOW_HISTORY_EMPTY;
            historySearchPlaceholder = AppStrings.SEARCH;
            historySearchFilters = FOLLOWER_HISTORY_SEARCH_FILTERS;
            historyColumns = FOLLOWER_HISTORY_COLUMNS;
            historyCountedFilterLabel =
              AppStrings.GAME_PASS_MONTHLY_COL_COUNTED_FOR_CURRENT_MONTH;
            historyRows = buildFollowerHistoryRows({
              progressTask,
              socialEvents: getEvents(socialEventsQuery.data),
            });
          } else if (isLoginChallenge) {
            historyLabel = AppStrings.GAME_PASS_MONTHLY_LOGIN_STREAK_HISTORY;
            historyEmptyText =
              AppStrings.GAME_PASS_MONTHLY_LOGIN_STREAK_HISTORY_EMPTY;
            historySearchPlaceholder = AppStrings.GAME_PASS_MONTHLY_SEARCH_BY_DATE;
            historyColumns = LOGIN_STREAK_HISTORY_COLUMNS;
            historyRows = buildLoginStreakHistoryRows({
              progressTask,
              socialEvents: getEvents(socialEventsQuery.data),
              fallbackUsername: enrollment?.username,
            });
          } else if (
            dataSource === "SOCIAL" ||
            challengeType === CHALLENGE_TYPE.SOCIAL
          ) {
            historyLabel = AppStrings.GAME_PASS_MONTHLY_SOCIAL_HISTORY;
            historyEmptyText = AppStrings.GAME_PASS_MONTHLY_SOCIAL_HISTORY_EMPTY;
            historyColumns = [
              AppStrings.USER_GAME_PASS_MONTHLY_COL_REFERRED_USER_ID,
              AppStrings.USER_GAME_PASS_MONTHLY_COL_SIGNUP_TIMESTAMP,
              AppStrings.USER_GAME_PASS_MONTHLY_COL_QUALIFYING_ACTION,
              AppStrings.USER_GAME_PASS_MONTHLY_COL_TIER_REACHED,
              AppStrings.USER_GAME_PASS_MONTHLY_COL_REWARD_ISSUED,
            ];
            if (eventType === TASK_EVENT_TYPE.P2P_PICK_WON) {
              historyLabel = AppStrings.USER_GAME_PASS_MONTHLY_REBET_HISTORY;
              historyEmptyText =
                AppStrings.USER_GAME_PASS_MONTHLY_HISTORY_EMPTY;
              historySearchPlaceholder = AppStrings.SEARCH;
              historySearchFilters = [
                {
                  key: "username",
                  label:
                    AppStrings.GAME_PASS_MONTHLY_COL_USERNAME_REBETTER,
                },
                { key: "betId", label: AppStrings.BET_ID },
                { key: "date", label: AppStrings.DATE },
              ];
              historyColumns = [
                AppStrings.BET_ID,
                AppStrings.GAME_PASS_MONTHLY_COL_USERNAME_REBETTER,
                AppStrings.USER_ID,
                AppStrings.GAME_PASS_MONTHLY_COL_REBET_AMOUNT,
                AppStrings.USER_GAME_PASS_MONTHLY_COL_REBET_VALIDITY,
                AppStrings.USER_GAME_PASS_MONTHLY_COL_FIRST_PURCHASE,
                AppStrings.USER_GAME_PASS_MONTHLY_COL_SIGNUP_TIMESTAMP,
              ];
            }
            historyRows = getEvents(socialEventsQuery.data)
              .filter((event) =>
                eventType ? event.event_type === eventType : true,
              )
              .map((event) => {
                const payload = event.payload || {};
                if (eventType === TASK_EVENT_TYPE.P2P_PICK_WON) {
                  // `||` on the display chains is deliberate: an empty-string
                  // bet id / username is as useless to an admin as a missing
                  // one, so it falls through to the next candidate.
                  const betId = payload.bet_id || event.event_id;
                  const rebetterUsername =
                    payload.username ||
                    payload.referred_username ||
                    event.user_id;
                  const rebetDate = formatHistoryTimestamp(
                    payload.signup_timestamp || event.occurred_at,
                  );
                  return {
                    id: event.event_id,
                    searchValues: {
                      username: rebetterUsername,
                      betId,
                      date: rebetDate,
                    },
                    cells: [
                      betId,
                      rebetterUsername,
                      payload.rebetter_user_id ?? payload.user_id ?? "-",
                      // formatHistoryResult passes non-numeric values through
                      // unchanged, so the trailing ?? "-" fires only when every
                      // amount field is absent — matching the row's other
                      // "absent" sentinels.
                      formatHistoryResult(
                        payload.rebet_amount ?? payload.amount ?? payload.wager,
                        currencyShortcode,
                      ) ?? "-",
                      payload.rebet_validity ?? payload.validity_flag ?? "-",
                      payload.first_purchase ??
                        payload.qualifying_action_completed ??
                        "-",
                      rebetDate,
                    ],
                  };
                }
                return {
                  id: event.event_id,
                  cells: [
                    payload.referred_user_id ||
                      payload.follower_user_id ||
                      payload.username ||
                      event.user_id,
                    formatHistoryTimestamp(event.occurred_at),
                    payload.qualifying_action_completed ?? payload.won,
                    payload.tier_reached,
                    payload.reward_issued,
                  ],
                };
              });
          }

          return (
            <LiveMonthlyChallengeCard
              key={challengeId}
              challengeId={challengeId}
              progressEntry={progressEntry}
              task={task}
              progressTask={progressTask}
              userId={userId}
              campaignId={campaignId}
              readOnly={readOnly}
              historyColumns={historyColumns}
              historyRows={historyRows}
              historyEmptyText={historyEmptyText}
              historyLabel={historyLabel}
              historySearchPlaceholder={historySearchPlaceholder}
              historySearchFilters={historySearchFilters}
              historyCountedFilterLabel={historyCountedFilterLabel}
              isLoginChallenge={isLoginChallenge}
              testId={`monthly-challenge-${challengeId}`}
            />
          );
        })}
      {!isLoading && !isError && challengeIds.length === 0 && (
        <p
          className="user-manager-game-pass-monthly-group-empty"
          data-testid={`${testId}-empty`}
        >
          {AppStrings.GAME_PASS_MONTHLY_EMPTY_COPY}
        </p>
      )}
    </MonthlyGroupSection>
  );
};

export default CampaignMonthGroup;
